import { PlusCircle, Split, Paperclip, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { api, eur, frDate, errMsg, resolveConfirmedDuplicate, PREP } from './api';

async function validateGuarded(id, body, confirm) {
    try { return await api.validate(id, body); } catch (e) {
        if (e?.response?.status !== 409 || !errMsg(e).startsWith('Doublon confirmé')) throw e;
        return resolveConfirmedDuplicate(id, e, confirm, () => api.validate(id, { ...body, forcer_doublon: true }));
    }
}

// Nouvelle dépense véhicule pour une ligne (toutes les gardes anti-doublon) — utilisé par la fiche et par la validation de plusieurs lignes
export async function createExpenseFor(id, plate, confirm) {
    let r = await validateGuarded(id, { destination: 'frais_vehicule' }, confirm);
    if (r === false) return false;
    if (r.status === 'doublon_document') {
        if (!(await confirm(`${r.message} : ${r.twin.fournisseur || ''} ${eur(r.twin.montant_ttc)}.\n\nValider quand même cette ligne ?`, { title: 'Doublon dans le même document', confirmLabel: 'Valider quand même', danger: true }))) return false;
        r = await api.validate(id, { destination: 'frais_vehicule', force_meme_document: true });
    }
    if (r.status !== 'doublon_detecte') return r;
    const list = (r.existing_frais || []).map(x => `• ${x.nom} ${eur(x.montant_ttc)} (${x.niveau})`).join('\n');
    if (!(await confirm(`Doublon détecté dans le dossier ${plate} :\n${list}\n\nCréer quand même une dépense supplémentaire ?`, { title: 'Doublon probable', confirmLabel: 'Créer quand même', danger: true }))) return false;
    return api.forceAttach(id);
}

export function makeHandlers({ d, v, vehicle, note, confirm, persist, run, dupWarn, setSplit, setLink }) {
    const recap = `${eur(v.montant_ttc)} TTC (HT ${eur(v.montant_ht)}, TVA ${v.tva_recuperable ? 'récupérable' : 'non récupérable'}) du ${frDate(v.date_facture)}`;
    return {
        linkFrais: (f) => setLink({ id: d.id, montant: v.montant_ttc, vehicle, frais: f, persist }),
        create: async () => {
            if (!vehicle) return;
            if (!(await confirm(`${PREP.on ? 'Préparer' : 'Créer'} une NOUVELLE dépense sur ${vehicle.immatriculation} (${vehicle.nom}) :\n${v.fournisseur || ''} ${v.numero_facture ? `n°${v.numero_facture}` : ''} — ${recap}.\n\n${PREP.on ? 'Préparation envoyée au gérant : rien n\'est comptabilisé avant sa validation.' : 'La marge et la commission provisoire seront recalculées automatiquement.'}${dupWarn}`, { title: PREP.on ? 'A — Préparer une nouvelle dépense' : 'A — Nouvelle dépense', confirmLabel: PREP.on ? 'Envoyer pour validation' : 'Créer la dépense' }))) return;
            run(async () => { await persist(); return createExpenseFor(d.id, vehicle.immatriculation, confirm); }, 'Dépense créée — marge et commission recalculées');
        },
        documentOnly: async () => {
            if (!(await confirm(`Classer cette facture dans le dossier ${vehicle.immatriculation} comme simple document, sans dépense ?`, { confirmLabel: 'Classer' }))) return;
            run(async () => { await persist(); return api.attachDocument(d.id, { vehicle_id: vehicle.id }); }, 'Document classé — aucune dépense');
        },
        wait: () => run(async () => { await persist(); return api.note(d.id, note); }, 'Laissée en attente — aucune conséquence financière'),
        restore: async () => {
            if (!(await confirm('Remettre cette facture en attente de contrôle ? Aucune dépense ne sera créée.', { confirmLabel: 'Remettre en attente' }))) return;
            run(() => api.restore(d.id), 'Facture remise en attente');
        },
        split: () => setSplit(true),
    };
}

export function InvoiceActions({ d, h, busy, hasVehicle }) {
    if (!d.traitable) {
        return (['supprime', 'archive'].includes(d.statut)
            ? <Button variant="outline" onClick={h.restore} disabled={busy} data-testid="invoice-restore"><Undo2 className="w-4 h-4 mr-1.5" />Remettre en attente</Button>
            : <p className="text-[12px] text-gray-500" data-testid="invoice-processed">Facture déjà traitée — consultation seule.</p>);
    }
    return (
        <div className="space-y-2" data-testid="invoice-actions">
            {!hasVehicle && <p className="text-[12px] text-amber-700">Choisissez un véhicule pour rattacher ou créer une dépense véhicule.</p>}
            <p className="text-[11px] text-gray-400">Le frais existe déjà ? Cliquez « Justificatif » sur sa ligne : aucun nouveau coût.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button onClick={h.create} disabled={busy || !hasVehicle} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="invoice-create"><PlusCircle className="w-4 h-4 mr-1.5" />Créer une nouvelle dépense</Button>
                <Button variant="outline" onClick={h.documentOnly} disabled={busy || !hasVehicle} data-testid="invoice-doc-only"><Paperclip className="w-4 h-4 mr-1.5" />Document seul, sans dépense</Button>
                <Button variant="outline" onClick={h.split} disabled={busy} className="sm:col-span-2" data-testid="invoice-split"><Split className="w-4 h-4 mr-1.5" />{d.repartition ? 'Reprendre la répartition préparée' : 'Répartir sur plusieurs véhicules'}</Button>
            </div>
        </div>
    );
}
